import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { MapToolsStore } from '@openlayers/MapToolsStore';
import { Measurement, CalcSurface } from '../../../../../../typings/reality-capture/volumes/models';
import { SurfaceEditor } from './measurement-extras';
import { store as trucksStore } from '@pages/trucks/store/rootStore';
import { MaterialEditor } from '@pages/trucks/materials/MaterialEditor';
import { Dropdown } from '@shared/ui-react/Dropdown';

export type MeasurementDataProps = {
    measurement: Measurement;
    measurements: Measurement[];
    toolsStore: MapToolsStore;
};

type MassesProps = {
    measurement: Measurement;
    density: number;
    selectedMaterialId: number | null;
    onDensityChange: (value: number) => void;
    onMaterialSelect: (id: number | null) => void;
};

const Masses = observer(({ measurement, density, selectedMaterialId, onDensityChange, onMaterialSelect }: MassesProps) => {
    const materials = trucksStore.materials.items;
    const selectedMaterial = selectedMaterialId != null
        ? materials.find(m => m.id === selectedMaterialId) ?? null
        : null;
    const selectedMaterialHasDensity = selectedMaterial?.m3LooseToTons != null;

    // Solid tonnage multiplies density by an editable factor that defaults to
    // 1 / m3LooseToM3Solid. Solid is only selectable when that ratio is defined.
    const looseToSolidRatio = selectedMaterial?.m3LooseToM3Solid ?? null;
    const canSelectSolid = looseToSolidRatio != null;
    const defaultSolidMultiplier = looseToSolidRatio ? Math.round((1 / looseToSolidRatio) * 1000) / 1000 : null;

    const [materialForm, setMaterialForm] = useState<'loose' | 'solid'>('loose');
    const [solidMultiplier, setSolidMultiplier] = useState<number | null>(null);
    useEffect(() => {
        if (!canSelectSolid && materialForm === 'solid') setMaterialForm('loose');
    }, [canSelectSolid, materialForm]);
    // Reset the editable multiplier to 1 / ratio whenever the selected material changes.
    useEffect(() => {
        setSolidMultiplier(defaultSolidMultiplier);
    }, [defaultSolidMultiplier]);

    const massFactor = materialForm === 'solid' && solidMultiplier && solidMultiplier > 0 ? solidMultiplier : 1;
    const fillMass = (measurement.fill_volume || 0) * density * massFactor;
    const cutMass = (measurement.cut_volume || 0) * density * massFactor;
    const netMass = fillMass - cutMass;
    const totalMass = fillMass + cutMass;
    const netFormatted = netMass < 0 ? netMass.toFixed(2) : `+${netMass.toFixed(2)}`;

    return (
        <div className='flex flex-col gap-4'>
            {/* Material selection */}
            <div className='flex items-center gap-2 flex-wrap'>
                <span className='text-sm font-medium'>{__('js_map_measuring_tools.material')}:</span>
                <div className='flex items-center gap-1'>
                    <select
                        value={selectedMaterialId ?? ''}
                        onChange={e => {
                            const val = e.target.value;
                            onMaterialSelect(val === '' ? null : parseInt(val, 10));
                        }}
                        className="border border-ik-border-default rounded px-2 py-0.5 text-sm bg-ik-surface-default text-ik-text-default"
                    >
                        <option value="">— {__('general.none')} —</option>
                        {materials.filter(m => m.m3LooseToTons != null).map(m => (
                            <option key={m.id} value={m.id}>
                                {m.material} ({m.m3LooseToTons} t/m³)
                            </option>
                        ))}
                    </select>
                    <button
                        className="p-1 text-ik-text-weak hover:text-ik-text-default transition-colors cursor-pointer"
                        onClick={() => MaterialEditor.open(trucksStore.materials, undefined, undefined, true, true)}
                        title={__('general.add')}
                    >
                        <i className="iki iki-plus text-sm" />
                    </button>
                </div>
            </div>

            {/* Density + volume type (loose / solid) on one line */}
            <div className='flex items-center gap-4 flex-wrap'>
                <div className='flex items-center gap-2'>
                    <span className='text-sm font-medium'>{__('js_map_measuring_tools.density')}:</span>
                    <input
                        type='number'
                        value={density}
                        onInput={e => onDensityChange(parseFloat(e.currentTarget.value))}
                        step={0.02}
                        min={0}
                        className="border border-ik-border-default rounded px-2 py-0.5 w-20 text-sm text-center bg-ik-surface-default"
                    />
                    <span className='text-sm text-ik-text-weak'>t/m³ {__('js_map_measuring_tools.loose')}</span>
                </div>

                <div className='flex items-center gap-2'>
                    <span className='text-sm font-medium'>{__('js_map_measuring_tools.volume_type')}:</span>
                    <Dropdown className="border border-ik-border-default rounded px-2 py-0.5 text-sm bg-ik-surface-default text-ik-text-default flex items-center" closeOnBodyClick styledBody={false}>
                        <span className="flex items-center gap-1 cursor-pointer">
                            <span>{materialForm === 'solid' ? __('js_map_measuring_tools.solid') : __('js_map_measuring_tools.loose')}</span>
                            <i className="iconf-caret text-gray-400" />
                        </span>
                        <ul className="dropdown-list clickable bg-white border border-gray-200 mt-1 rounded-md shadow-lg text-sm z-50">
                            <li onClick={() => setMaterialForm('loose')} className="unstyled cursor-pointer hover:!text-ik-accent-primary transition-colors">
                                {__('js_map_measuring_tools.loose')}
                            </li>
                            {canSelectSolid ? (
                                <li onClick={() => setMaterialForm('solid')} className="unstyled cursor-pointer hover:!text-ik-accent-primary transition-colors">
                                    {__('js_map_measuring_tools.solid')}
                                </li>
                            ) : (
                                <li
                                    className="disabled bg-ik-surface-dim !text-ik-text-weakest italic"
                                    title={__('js_map_measuring_tools.no_solid_ratio_for_material')}
                                    aria-disabled="true"
                                >
                                    {__('js_map_measuring_tools.solid')}
                                </li>
                            )}
                        </ul>
                    </Dropdown>
                    {materialForm === 'solid' && (
                        <span className='flex items-center gap-1 text-sm text-ik-text-weak'>
                            ×
                            <input
                                type='number'
                                value={solidMultiplier ?? ''}
                                onChange={e => setSolidMultiplier(e.currentTarget.value === '' ? null : parseFloat(e.currentTarget.value))}
                                step={0.01}
                                min={0}
                                className="border border-ik-border-default rounded px-2 py-0.5 w-16 text-sm text-center bg-ik-surface-default"
                            />
                        </span>
                    )}
                </div>

                {selectedMaterialId != null && !selectedMaterialHasDensity && (
                    <span className='text-xs text-ik-text-weak italic'>
                        {__('js_map_measuring_tools.no_density_for_material')}
                    </span>
                )}

                {selectedMaterialId != null && !canSelectSolid && (
                    <span className='text-xs text-ik-text-weak italic'>
                        {__('js_map_measuring_tools.no_solid_ratio_for_material')}
                    </span>
                )}
            </div>

            {/* Mass table */}
            <div className="flex flex-col w-full">
                <div className="flex items-center justify-between bg-ik-surface-dim rounded-md p-3 border border-ik-border-subtle">
                    <div className="flex flex-col items-center flex-1 border-r border-ik-border-subtle">
                        <span className="text-xs text-ik-text-weak uppercase tracking-wider mb-1">{__('js_map_measuring_tools.fill')}</span>
                        <span className="text-sm font-semibold text-green-600">{fillMass.toFixed(2)} {__('js_map_measuring_tools.tons')}</span>
                    </div>

                    <div className="flex flex-col items-center flex-1 border-r border-ik-border-subtle">
                        <span className="text-xs text-ik-text-weak uppercase tracking-wider mb-1">{__('js_map_measuring_tools.cut')}</span>
                        <span className="text-sm font-semibold text-red-600">{cutMass.toFixed(2)} {__('js_map_measuring_tools.tons')}</span>
                    </div>

                    <div className="flex flex-col items-center flex-1 border-r border-ik-border-subtle relative">
                        <div className="flex items-center gap-1 mb-1">
                            <span className="text-xs text-ik-text-weak uppercase tracking-wider">{__('js_map_measuring_tools.net')}</span>
                            <i className="iki iki-info text-ik-text-weak text-xs cursor-help" title={__('js_map_measuring_tools.net_info')}></i>
                        </div>
                        <span className="text-sm font-semibold" style={{ color: netMass < 0 ? '#dc2626' : '#16a34a' }}>
                            {netFormatted} {__('js_map_measuring_tools.tons')}
                        </span>
                    </div>

                    <div className="flex flex-col items-center flex-1 relative">
                        <div className="flex items-center gap-1 mb-1">
                            <span className="text-xs text-ik-text-weak uppercase tracking-wider">{__('js_map_measuring_tools.total')}</span>
                            <i className="iki iki-info text-ik-text-weak text-xs cursor-help" title={__('js_map_measuring_tools.total_info')}></i>
                        </div>
                        <span className="text-sm font-semibold text-ik-text-strong">
                            {totalMass.toFixed(2)} {__('js_map_measuring_tools.tons')}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
});

export const MeasurementData = ({ measurement, toolsStore }: MeasurementDataProps) => {
    const volumeTool = toolsStore.volumeTool;
    const [tolerance, setTolerance] = useState<number>(measurement.tolerance ?? 0.1);
    const [toleranceDraft, setToleranceDraft] = useState<number>(measurement.tolerance ?? 0.1);
    const [baseHeight, setBaseHeight] = useState<number>(measurement.baseHeight ?? 0);
    const [baseHeightDraft, setBaseHeightDraft] = useState<number>(measurement.baseHeight ?? 0);
    const [isSettingsExpanded, setIsSettingsExpanded] = useState(false);
    const [isMassesExpanded, setIsMassesExpanded] = useState(false);
    const [isEditingTolerance, setIsEditingTolerance] = useState(false);
    const [isEditingBaseHeight, setIsEditingBaseHeight] = useState(false);
    const [density, setDensity] = useState<number>(1.2);
    const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(null);

    useEffect(() => {
        setBaseHeight(measurement.baseHeight);
        setTolerance(measurement.tolerance);
    }, [measurement]);

    useEffect(() => {
        if (trucksStore.materials.items.length === 0) {
            trucksStore.materials.fetch();
        }
    }, []);

    const handleMaterialSelect = (id: number | null): void => {
        setSelectedMaterialId(id);
        if (id === null) return;
        const material = trucksStore.materials.items.find(m => m.id === id);
        if (material?.m3LooseToTons != null) {
            setDensity(material.m3LooseToTons);
        }
    };

    const handleBaseHeightEditStart = (): void => {
        setBaseHeightDraft(baseHeight);
        setIsEditingBaseHeight(true);
    };

    const handleBaseHeightSave = (): void => {
        setBaseHeight(baseHeightDraft);
        measurement.baseHeight = baseHeightDraft;
        volumeTool.updateVolume(measurement);
        setIsEditingBaseHeight(false);
    };

    const handleBaseHeightCancel = (): void => {
        setIsEditingBaseHeight(false);
    };

    const handleToleranceEditStart = (): void => {
        setToleranceDraft(tolerance);
        setIsEditingTolerance(true);
    };

    const handleToleranceSave = (): void => {
        setTolerance(toleranceDraft);
        measurement.tolerance = toleranceDraft;
        volumeTool.updateVolume(measurement);
        setIsEditingTolerance(false);
    };

    const handleToleranceCancel = (): void => {
        setIsEditingTolerance(false);
    };

    const handleReferenceChange = (reference: CalcSurface) => {
        measurement.calcReference = reference;
        volumeTool.updateVolume(measurement);
    };

    const handleTargetChange = (target: CalcSurface) => {
        measurement.calcTarget = target;
        volumeTool.updateVolume(measurement);
    };

    const Results = ({ measurement }: { measurement: Measurement }) => {
        const fillVolume = (measurement.fill_volume || 0);
        const cutVolume = (measurement.cut_volume || 0);
        const netVolume = fillVolume - cutVolume;
        const totalVolume = fillVolume + cutVolume;
        const netFormatted = netVolume < 0 ? netVolume.toFixed(2) : `+${netVolume.toFixed(2)}`;
        return (
            <div className="flex flex-col w-full">
                <div className="flex items-center justify-between bg-ik-surface-dim rounded-md p-3 border border-ik-border-subtle">
                    <div className="flex flex-col items-center flex-1 border-r border-ik-border-subtle">
                        <span className="text-xs text-ik-text-weak uppercase tracking-wider mb-1">{__('js_map_measuring_tools.fill')}</span>
                        <span className="text-sm font-semibold text-green-600">{fillVolume.toFixed(2)} m³</span>
                    </div>

                    <div className="flex flex-col items-center flex-1 border-r border-ik-border-subtle">
                        <span className="text-xs text-ik-text-weak uppercase tracking-wider mb-1">{__('js_map_measuring_tools.cut')}</span>
                        <span className="text-sm font-semibold text-red-600">{cutVolume.toFixed(2)} m³</span>
                    </div>

                    <div className="flex flex-col items-center flex-1 border-r border-ik-border-subtle relative">
                        <div className="flex items-center gap-1 mb-1">
                            <span className="text-xs text-ik-text-weak uppercase tracking-wider">{__('js_map_measuring_tools.net')}</span>
                            <i className="iki iki-info text-ik-text-weak text-xs cursor-help" title={__('js_map_measuring_tools.net_info')}></i>
                        </div>
                        <span className="text-sm font-semibold" style={{ color: netVolume < 0 ? '#dc2626' : '#16a34a' }}>
                            {netFormatted} m³
                        </span>
                    </div>

                    <div className="flex flex-col items-center flex-1 relative">
                        <div className="flex items-center gap-1 mb-1">
                            <span className="text-xs text-ik-text-weak uppercase tracking-wider">{__('js_map_measuring_tools.total')}</span>
                            <i className="iki iki-info text-ik-text-weak text-xs cursor-help" title={__('js_map_measuring_tools.total_info')}></i>
                        </div>
                        <span className="text-sm font-semibold text-ik-text-strong">
                            {totalVolume.toFixed(2)} m³
                        </span>
                    </div>
                </div>
            </div>
        )
    }

    const Settings = ({
        isSettingsExpanded,
        isEditingTolerance,
        tolerance,
        toleranceDraft,
        measurement,
        isEditingBaseHeight,
        baseHeight,
        baseHeightDraft,
        toolsStore,
    }) => {
        return (
            <div className="flex flex-col w-full bg-gray-50/50 rounded border border-gray-200 overflow-hidden">
                <div
                    className="flex items-center gap-2 text-xs font-semibold text-gray-800 p-3 cursor-pointer select-none bg-white border-b border-gray-100"
                    onClick={() => setIsSettingsExpanded(!isSettingsExpanded)}
                >
                    {isSettingsExpanded ? <i className="pi pi-chevron-down text-gray-500 text-xs"></i> : <i className="pi pi-chevron-right text-gray-500 text-xs"></i>}
                    {__('general.settings')}
                </div>

                {isSettingsExpanded && (
                    <div className="p-4 flex flex-col gap-4">
                        {/* Parameters Grid */}
                        <div className="flex items-center gap-6">
                            {/* Tolerance */}
                            <div className="flex items-center gap-2">
                                {!isEditingTolerance && (
                                    <i
                                        className="iki iki-edit cursor-pointer text-ik-text-weak hover:text-ik-text-strong"
                                        onClick={handleToleranceEditStart}
                                        title={__('js_map_measuring_tools.edit_tolerance')}
                                    ></i>
                                )}
                                <span className="text-sm font-medium">{__('js_map_measuring_tools.tolerance')}:</span>
                                {isEditingTolerance ? (
                                    <>
                                        <input
                                            type='number'
                                            value={toleranceDraft}
                                            step={0.05}
                                            min={0}
                                            onChange={e => setToleranceDraft(parseFloat(e.currentTarget.value))}
                                            className="border border-ik-border-default rounded px-2 py-0.5 w-16 text-sm text-center bg-ik-surface-default"
                                        />
                                        <i
                                            className="iki iki-check cursor-pointer text-green-600 hover:text-green-700"
                                            onClick={handleToleranceSave}
                                            title={__('general.save')}
                                        />
                                        <i
                                            className="iki iki-cross cursor-pointer text-ik-text-weak hover:text-ik-text-strong"
                                            onClick={handleToleranceCancel}
                                            title={__('general.cancel')}
                                        />
                                    </>
                                ) : (
                                    <span className="text-sm px-2">{tolerance}</span>
                                )}
                                <span className="text-sm">m</span>
                            </div>

                            {/* Base Height */}
                            {measurement.baseHeight !== undefined && (
                                <div className='flex items-center gap-2'>
                                    {!isEditingBaseHeight && (
                                        <i
                                            className="iki iki-edit cursor-pointer text-ik-text-weak hover:text-ik-text-strong"
                                            onClick={handleBaseHeightEditStart}
                                            title={__('js_map_measuring_tools.edit_base_height')}
                                        ></i>
                                    )}
                                    <span className='text-sm font-medium'>{__('js_map_measuring_tools.base_height')}:</span>
                                    {isEditingBaseHeight ? (
                                        <>
                                            <input
                                                type='number'
                                                value={baseHeightDraft.toFixed(2)}
                                                onChange={e => setBaseHeightDraft(parseFloat(e.currentTarget.value))}
                                                step={0.1}
                                                className="border border-ik-border-default rounded px-2 py-0.5 w-20 text-sm text-center bg-ik-surface-default"
                                            />
                                            <i
                                                className="iki iki-check cursor-pointer text-green-600 hover:text-green-700"
                                                onClick={handleBaseHeightSave}
                                                title={__('general.save')}
                                            />
                                            <i
                                                className="iki iki-cross cursor-pointer text-ik-text-weak hover:text-ik-text-strong"
                                                onClick={handleBaseHeightCancel}
                                                title={__('general.cancel')}
                                            />
                                        </>
                                    ) : (
                                        <span className="text-sm px-2">{baseHeight.toFixed(2)}</span>
                                    )}
                                    <span className='text-sm'>m</span>
                                </div>
                            )}
                        </div>

                        {/* Surfaces Row */}
                        <div className="flex items-start w-full">
                            {/* Left Column */}
                            <div className="flex-1 min-w-0 pr-6">
                                <div className="text-sm font-semibold text-ik-text-weak mb-2">
                                    {__('js_map_measuring_tools.surface_1')}
                                </div>
                                <SurfaceEditor kind='reference' toolsStore={toolsStore} measurement={measurement} onChange={handleReferenceChange} />
                            </div>

                            {/* Right Column */}
                            {measurement.type !== 'stockpile' && (
                                <div className="flex-1 min-w-0 pl-6">
                                    <div className="text-sm font-semibold text-ik-text-weak mb-2">
                                        {__('js_map_measuring_tools.surface_2')}
                                    </div>
                                    {measurement.type === 'comparison' ? (
                                        <SurfaceEditor kind='target' toolsStore={toolsStore} measurement={measurement} onChange={handleTargetChange} />
                                    ) : (
                                        <div className="text-sm text-ik-text-weak italic mt-1">N/A</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="flex flex-col w-full gap-3">

            {/* Results Section */}
            <Results
                measurement={measurement}
            />

            {/* Settings */}
            <Settings
                isSettingsExpanded={isSettingsExpanded}
                isEditingTolerance={isEditingTolerance}
                tolerance={tolerance}
                toleranceDraft={toleranceDraft}
                measurement={measurement}
                isEditingBaseHeight={isEditingBaseHeight}
                baseHeight={baseHeight}
                baseHeightDraft={baseHeightDraft}
                toolsStore={toolsStore}
            />

            {/* Masses */}
            <div className="flex flex-col w-full bg-gray-50/50 rounded border border-gray-200 overflow-hidden">
                <div
                    className="flex items-center gap-2 text-xs font-semibold text-gray-800 p-3 cursor-pointer select-none bg-white border-b border-gray-100"
                    onClick={() => setIsMassesExpanded(!isMassesExpanded)}
                >
                    {isMassesExpanded ? <i className="pi pi-chevron-down text-gray-500 text-xs"></i> : <i className="pi pi-chevron-right text-gray-500 text-xs"></i>}
                    <i className="iki iki-layers text-gray-600 text-sm"></i>
                    {__('js_map_measuring_tools.masses')}
                </div>

                {isMassesExpanded && (
                    <div className="p-4 flex flex-col gap-4">
                        <Masses
                            measurement={measurement}
                            density={density}
                            selectedMaterialId={selectedMaterialId}
                            onDensityChange={setDensity}
                            onMaterialSelect={handleMaterialSelect}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};
